import { Trans, useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import { HStack, VStack } from '~/components/layout/Stack';
import ModalActions from '~/components/modals/Modal/ModalActions';
import Text from '~/components/typographies/Text';
import { useAnalytics } from '~/hooks/useAnalytics';
import IconSearch from '~/icons/icon-search.svg?react';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import styles from './CheckCompany.module.scss';

export interface ExistingCompany {
  name: string;
  vatNumber: string;
  taxNumber: string;
  address: string;
  companyIdentityId: string;
}
interface CheckCompanyProps extends ModalProps {
  companyData: ExistingCompany;
  onSearch: () => void;
  onNext: (value: ExistingCompany) => void;
  isLoading: boolean;
}

const CheckCompany: React.FC<CheckCompanyProps> = ({ closeModal, onSearch, companyData, onNext, isLoading }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'add_company.steps.check_company' });
  const { track } = useAnalytics();

  return (
    <VStack gap={5}>
      <Text variant="body2" weight="regular">
        <Trans t={t} i18nKey="description" />
      </Text>
      <VStack gap={2} className={styles.infoContainer}>
        <HStack gap={2}>
          <Text variant="body2" weight="regular" className={styles.title}>
            {t('summary.name')}
          </Text>
          <Text variant="body2" weight="semibold" className={styles.value}>
            {companyData.name}
          </Text>
        </HStack>
        <HStack gap={2}>
          <Text variant="body2" weight="regular" className={styles.title}>
            {t('summary.address')}
          </Text>
          <Text variant="body2" weight="semibold" className={styles.value}>
            {companyData.address}
          </Text>
        </HStack>
        <HStack gap={2}>
          <Text variant="body2" weight="regular" className={styles.title}>
            {t('summary.tax_code')}
          </Text>
          <Text variant="body2" weight="semibold" className={styles.value}>
            {companyData.taxNumber}
          </Text>
        </HStack>
        <HStack gap={2}>
          <Text variant="body2" weight="regular" className={styles.title}>
            {t('summary.vat_number')}
          </Text>
          <Text variant="body2" weight="semibold" className={styles.value}>
            {companyData.vatNumber}
          </Text>
        </HStack>
        <div className={styles.searchButtonContainer}>
          <Button variant="link" onClick={onSearch} Icon={IconSearch}>
            {t('actions.search')}
          </Button>
        </div>
      </VStack>
      <ModalActions className={styles.actionsContainer}>
        <Button
          variant={'outlined'}
          onClick={() => {
            closeModal();
          }}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={() => {
            track(AnalyticsEvents.SECOND_COMPANIES_CREATION_STARTED, {
              step: 'company_data_confirmed',
              metadata: { tax_code: companyData.taxNumber },
            });
            onNext(companyData);
          }}
          loading={isLoading}
        >
          {t('actions.next')}
        </Button>
      </ModalActions>
    </VStack>
  );
};

export default CheckCompany;
